import type { RoomSettings } from "@hitster/shared";
import { DEFAULT_BINGO_SETTINGS } from "@hitster/shared";
import { IncludeMusicGuessOption } from "./IncludeMusicGuessOption";
import { HintsOption } from "./HintsOption";
import { BingoSettingsForm } from "./BingoSettingsForm";
import type { BingoCatalogValidation } from "@hitster/shared";

interface Props {
  settings: RoomSettings;
  onChange: (patch: Partial<RoomSettings>) => void;
  connectedPlayerCount?: number;
  bingoValidation?: BingoCatalogValidation;
}

export function RoomSettingsForm({ settings, onChange, connectedPlayerCount, bingoValidation }: Props) {
  const gameKind = settings.gameKind ?? "timeline";

  return (
    <div className="space-y-4">
      <fieldset>
        <legend className="text-sm mb-2">Mode de jeu</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            className={`cw-btn ${gameKind === "timeline" ? "cw-btn-primary" : "cw-btn-secondary"}`}
            onClick={() => onChange({ gameKind: "timeline" })}
          >
            🕰️ Hitster classique / Frise
          </button>
          <button
            type="button"
            className={`cw-btn ${gameKind === "bingo" ? "cw-btn-primary" : "cw-btn-secondary"}`}
            onClick={() =>
              onChange({
                gameKind: "bingo",
                bingo: settings.bingo ?? { ...DEFAULT_BINGO_SETTINGS },
              })
            }
          >
            🎱 Hitster Bingo
          </button>
        </div>
      </fieldset>

      {gameKind === "bingo" ? (
        <BingoSettingsForm settings={settings} onChange={onChange} validation={bingoValidation} />
      ) : (
        <>
          <label className="block">
            <span className="text-sm">Cartes pour gagner</span>
            <input
              type="number"
              className="cw-input mt-1"
              min={1}
              max={50}
              value={settings.winTarget}
              onChange={(e) => onChange({ winTarget: Number(e.target.value) })}
            />
          </label>

          <label className="block">
            <span className="text-sm">Durée manche (sec)</span>
            <input
              type="number"
              className="cw-input mt-1"
              min={15}
              max={180}
              value={settings.roundDurationSeconds}
              onChange={(e) => onChange({ roundDurationSeconds: Number(e.target.value) })}
            />
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.singleListen}
              onChange={(e) => onChange({ singleListen: e.target.checked })}
            />
            <span>Écoute unique par manche</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.continueAfterWin !== false}
              onChange={(e) => onChange({ continueAfterWin: e.target.checked })}
            />
            <span>Laisser la possibilité de continuer quand quelqu&apos;un a gagné</span>
          </label>

          <HintsOption settings={settings} onChange={onChange} />

          <label className="block">
            <span className="text-sm">Mode frise</span>
            <select
              className="cw-input mt-1"
              value={settings.mode}
              onChange={(e) =>
                onChange({
                  mode: e.target.value as "individual" | "shared",
                })
              }
            >
              <option value="individual">Frise individuelle</option>
              <option value="shared">Frise commune</option>
            </select>
          </label>
        </>
      )}

      <label className="block">
        <span className="text-sm">Joueurs max</span>
        <input
          type="number"
          className="cw-input mt-1"
          min={2}
          max={12}
          value={settings.maxPlayers}
          onChange={(e) => onChange({ maxPlayers: Number(e.target.value) })}
        />
        {connectedPlayerCount != null && connectedPlayerCount > 0 && (
          <p className="text-xs text-violet-400/60 mt-1">
            {connectedPlayerCount} joueur{connectedPlayerCount > 1 ? "s" : ""} déjà connecté
            {connectedPlayerCount > 1 ? "s" : ""}
          </p>
        )}
      </label>

      <IncludeMusicGuessOption
        checked={!!settings.includeMusicGuess}
        onChange={(includeMusicGuess) => onChange({ includeMusicGuess })}
      />

      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm">Année min (opt.)</span>
          <input
            type="number"
            className="cw-input mt-1"
            placeholder="vide = tout"
            value={settings.minYear ?? ""}
            onChange={(e) => {
              const raw = e.target.value.trim();
              onChange({
                minYear: raw === "" || Number.isNaN(Number(raw)) ? undefined : Number(raw),
              });
            }}
          />
        </label>
        <label className="block">
          <span className="text-sm">Année max (opt.)</span>
          <input
            type="number"
            className="cw-input mt-1"
            placeholder="vide = tout"
            value={settings.maxYear ?? ""}
            onChange={(e) => {
              const raw = e.target.value.trim();
              onChange({
                maxYear: raw === "" || Number.isNaN(Number(raw)) ? undefined : Number(raw),
              });
            }}
          />
        </label>
      </div>

      <label className="block">
        <span className="text-sm">Mot de passe (opt.)</span>
        <input
          type="password"
          className="cw-input mt-1"
          value={settings.password ?? ""}
          onChange={(e) =>
            onChange({
              password: e.target.value || undefined,
            })
          }
        />
      </label>
    </div>
  );
}
